<template lang="pug">
form.container.w-50.login-page
    h3.mb-1 Biobank Editor
    p.text-muted.mb-4 Version {{appVersion}}
    .alert.alert-warning(v-if="authMessage") {{authMessage}}
    .alert.alert-info(v-if="showOidcFinalizing") Logging in...
    .alert.alert-info(v-if="showLoadingAuthState") Loading authentication mode...
    .card.mb-3(v-if="canChooseMethod && !showOidcFinalizing")
        .card-body.py-3
            .btn-group.btn-group-toggle.d-flex.login-method-switch(role="group" aria-label="Login method")
                button.btn(
                    type="button"
                    :class="activeMethod === 'local' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="setActiveMethod('local')"
                ) Biobank Editor Local Login
                button.btn(
                    type="button"
                    :class="activeMethod === 'oidc' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="setActiveMethod('oidc')"
                ) Life Science Login
    .card.mb-4(v-if="showPasswordCard && !showOidcFinalizing")
        .card-body
            .form-group
                label Email address
                input.form-control(v-model="email")
            .form-group
                label Password
                input.form-control(type="password", v-model="password")
            button.btn.btn-primary.btn-block(type="button" @click="loginLocal" :disabled="!(email && password) || localLoginSubmitting")
                span.spinner-border.spinner-border-sm.mr-2(v-if="localLoginSubmitting" role="status" aria-hidden="true")
                | {{ localLoginSubmitting ? 'Signing in...' : 'Login as Biobank Editor User' }}
            p.text-danger.mt-3.mb-0(v-if="err && !loadingAuthState") {{err.message}}
    .card.mb-4(v-if="showOidcCard && !showOidcFinalizing")
        .card-body
            .text-center
                button.ls-login-button(type="button" @click="startOidcLogin" :disabled="oidcStarting || loadingAuthState")
                    img.ls-login-button-image(
                        src="https://lifescience-ri.eu/fileadmin/lifescience-ri/media/Images/button-login.svg"
                        alt="LS Login"
                    )
            p.text-danger.mt-3.mb-0(v-if="oidcErrorMessage") {{oidcErrorMessage}}
    p.mt-3.mb-0.text-center
        a(:href="'privacy-policy.html'") Privacy Policy
    p.text-muted.mt-3.mb-0.text-center(v-if="!loadingAuthState && !showPasswordLogin && !showOidcLogin")
        | No authentication method is currently enabled.
</template>

<script>
import {login, getAuthConfig, getOidcStart, getSession} from "../api";
import buildVersion from "../generated-build-version.json";

export default {
    data() {
        const runtimeConfig = (typeof window !== "undefined" && window.__APP_CONFIG__)
            ? window.__APP_CONFIG__
            : null;
        const isOrdsRuntime = !!(runtimeConfig && runtimeConfig.ordsSchema);
        return {
            appVersion: isOrdsRuntime ? buildVersion.versionOrds : buildVersion.versionNode,
            err: null,
            authMessage: null,
            authConfig: null,
            loadingAuthState: true,
            showLoadingAuthState: false,
            loadingStateHandle: null,
            oidcErrorMessage: "",
            oidcStarting: false,
            localLoginSubmitting: false,
            email: "",
            password: "",
            activeMethod: "local",
            showOidcFinalizing: false,
            oidcFinalizePollHandle: null,
            oidcFinalizeDeadlineMs: 0
        };
    },
    computed: {
        showPasswordLogin() {
            return !!(this.authConfig && this.authConfig.local_password_login_allowed);
        },
        showOidcLogin() {
            return !!(this.authConfig && this.authConfig.oidc_enabled);
        },
        canChooseMethod() {
            return this.showPasswordLogin && this.showOidcLogin;
        },
        showPasswordCard() {
            if (!this.showPasswordLogin) return false;
            if (!this.canChooseMethod) return true;
            return this.activeMethod === "local";
        },
        showOidcCard() {
            if (!this.showOidcLogin) return false;
            if (!this.canChooseMethod) return true;
            return this.activeMethod === "oidc";
        }
    },
    created() {
        const message = localStorage.getItem("auth_message");
        if (message) {
            this.authMessage = message;
            localStorage.removeItem("auth_message");
        }
        const oidcError = this.$route && this.$route.query ? this.$route.query.oidc_error : "";
        if (oidcError) {
            this.oidcErrorMessage = oidcError;
            this.clearOidcLoginProgressMarkers();
            localStorage.removeItem("post_login_redirect_pending");
        }
        this.initializeActiveMethod();
        this.startOidcFinalizeIfNeeded();
        this.loadAuthConfig();
    },
    beforeDestroy() {
        if (this.loadingStateHandle) {
            clearTimeout(this.loadingStateHandle);
            this.loadingStateHandle = null;
        }
        if (this.oidcFinalizePollHandle) {
            clearTimeout(this.oidcFinalizePollHandle);
            this.oidcFinalizePollHandle = null;
        }
    },
    methods: {
        initializeActiveMethod() {
            const storedMethod = localStorage.getItem("last_login_method");
            if (storedMethod === "oidc" || storedMethod === "local") {
                this.activeMethod = storedMethod;
            }
        },
        rememberLoginMethod(method) {
            localStorage.setItem("last_login_method", method);
        },
        setActiveMethod(method) {
            this.activeMethod = method;
        },
        syncAvailableMethod() {
            if (this.showPasswordLogin && !this.showOidcLogin) {
                this.activeMethod = "local";
                return;
            }
            if (this.showOidcLogin && !this.showPasswordLogin) {
                this.activeMethod = "oidc";
                return;
            }
            if (!this.canChooseMethod) return;
            if (this.activeMethod !== "local" && this.activeMethod !== "oidc") {
                this.activeMethod = "local";
            }
        },
        updateLocalSession(user) {
            localStorage.setItem("user", user.email);
            localStorage.setItem("is_admin", user.is_admin ? "1" : "0");
            localStorage.setItem("allowed_domains", JSON.stringify(user.allowed_domains || []));
            if (user.biobank) localStorage.setItem("biobank", user.biobank);
            else localStorage.removeItem("biobank");
        },
        isOidcLoginInProgress() {
            if (typeof sessionStorage === "undefined") return false;
            return sessionStorage.getItem("oidc_login_in_progress") === "1";
        },
        getOidcLoginStartedAtMs() {
            const raw = localStorage.getItem("oidc_login_started_at_ms");
            const value = Number(raw);
            if (!Number.isFinite(value) || value <= 0) return 0;
            return value;
        },
        hasRecentOidcLoginAttempt() {
            const startedAtMs = this.getOidcLoginStartedAtMs();
            if (!startedAtMs) return false;
            return (Date.now() - startedAtMs) < 120000;
        },
        clearOidcLoginProgressMarkers() {
            if (typeof sessionStorage !== "undefined") {
                sessionStorage.removeItem("oidc_login_in_progress");
            }
            localStorage.removeItem("oidc_login_started_at_ms");
        },
        hasOidcCallbackParamsInUrl() {
            if (typeof window === "undefined") return false;
            const searchParams = new URLSearchParams(window.location.search || "");
            if (
                searchParams.has("code") ||
                searchParams.has("state") ||
                searchParams.has("session") ||
                searchParams.has("oidc_error")
            ) return true;

            const hash = window.location.hash || "";
            const queryIndex = hash.indexOf("?");
            if (queryIndex < 0) return false;
            const hashParams = new URLSearchParams(hash.slice(queryIndex + 1));
            return (
                hashParams.has("code") ||
                hashParams.has("state") ||
                hashParams.has("session") ||
                hashParams.has("oidc_error")
            );
        },
        isLikelyLsOidcReturn() {
            if (typeof document === "undefined") return false;
            const referrer = String(document.referrer || "").toLowerCase();
            if (!referrer) return false;
            return referrer.includes("lifescience-ri.eu") || referrer.includes("/oidc/");
        },
        scheduleOidcFinalizePoll() {
            if (this.oidcFinalizePollHandle) {
                clearTimeout(this.oidcFinalizePollHandle);
            }
            this.oidcFinalizePollHandle = setTimeout(() => {
                this.tryFinalizeOidcLogin();
            }, 700);
        },
        tryFinalizeOidcLogin() {
            if (!this.showOidcFinalizing) {
                this.showOidcFinalizing = false;
                return;
            }
            if (this.oidcFinalizeDeadlineMs && Date.now() > this.oidcFinalizeDeadlineMs) {
                this.showOidcFinalizing = false;
                this.clearOidcLoginProgressMarkers();
                return;
            }
            getSession()
            .then(user => {
                this.updateLocalSession(user);
                this.clearOidcLoginProgressMarkers();
                this.showOidcFinalizing = false;
                this.$router.replace("/").catch(() => {});
            })
            .catch(() => {
                this.scheduleOidcFinalizePoll();
            });
        },
        startOidcFinalizeIfNeeded() {
            const oidcError = this.$route && this.$route.query ? this.$route.query.oidc_error : "";
            const forcedByReturnContext = this.hasOidcCallbackParamsInUrl()
                || (
                    localStorage.getItem("last_login_method") === "oidc"
                    && this.isLikelyLsOidcReturn()
                );
            const shouldFinalize = this.isOidcLoginInProgress()
                || forcedByReturnContext
                || this.hasRecentOidcLoginAttempt();
            if (oidcError || !shouldFinalize) {
                this.showOidcFinalizing = false;
                return;
            }
            const startedAtMs = this.getOidcLoginStartedAtMs();
            this.oidcFinalizeDeadlineMs = startedAtMs ? (startedAtMs + 120000) : (Date.now() + 120000);
            this.showOidcFinalizing = true;
            this.tryFinalizeOidcLogin();
        },
        loginLocal() {
            this.err = null;
            this.oidcErrorMessage = "";
            this.localLoginSubmitting = true;
            login(this.email.trim(), this.password)
            .then(user => {
                this.rememberLoginMethod("local");
                this.updateLocalSession(user);
                return this.$router.push("/").catch(routerError => {
                    if (!routerError || routerError.name !== "NavigationDuplicated") {
                        const message = String(routerError && routerError.message || "");
                        if (!message.includes('Redirected when going from "/login" to "/"')) {
                            throw routerError;
                        }
                    }
                });
            })
            .catch(err => {
                this.err = err;
            })
            .finally(() => {
                this.localLoginSubmitting = false;
            });
        },
        loadAuthConfig() {
            this.loadingAuthState = true;
            this.showLoadingAuthState = false;
            if (this.loadingStateHandle) clearTimeout(this.loadingStateHandle);
            this.loadingStateHandle = setTimeout(() => {
                this.showLoadingAuthState = true;
                this.loadingStateHandle = null;
            }, 300);
            getAuthConfig()
            .then(config => {
                this.authConfig = config;
                this.syncAvailableMethod();
            })
            .catch(err => {
                console.warn("Auth config load failed", err);
            })
            .finally(() => {
                if (this.loadingStateHandle) {
                    clearTimeout(this.loadingStateHandle);
                    this.loadingStateHandle = null;
                }
                this.loadingAuthState = false;
                this.showLoadingAuthState = false;
            });
        },
        resolveOidcStartTargetUrl(startResponse) {
            const payload = (startResponse && typeof startResponse === "object") ? startResponse : {};
            const directUrl = [
                payload.authorize_url,
                payload.redirect_url
            ]
            .find(value => {
                if (typeof value !== "string") return false;
                const normalized = value.trim().toLowerCase();
                return normalized !== "" && normalized !== "undefined" && normalized !== "null";
            });
            if (directUrl) return directUrl.trim();

            const authorizationEndpoint = String(payload.authorization_endpoint || "").trim();
            const clientId = String(payload.client_id || "").trim();
            const redirectUri = String(payload.redirect_uri || "").trim();
            const state = String(payload.state || "").trim();
            if (!authorizationEndpoint || !clientId || !redirectUri || !state) return "";

            const responseType = String(payload.response_type || "code").trim() || "code";
            const scope = String(payload.scope || "openid profile email").trim() || "openid profile email";

            const params = new URLSearchParams();
            params.set("response_type", responseType);
            params.set("client_id", clientId);
            params.set("redirect_uri", redirectUri);
            params.set("scope", scope);
            params.set("state", state);

            return authorizationEndpoint + (authorizationEndpoint.includes("?") ? "&" : "?") + params.toString();
        },
        startOidcLogin() {
            this.oidcStarting = true;
            this.err = null;
            this.oidcErrorMessage = "";
            this.rememberLoginMethod("oidc");
            localStorage.setItem("oidc_login_started_at_ms", String(Date.now()));
            localStorage.setItem("post_login_redirect_pending", "1");
            if (typeof sessionStorage !== "undefined") {
                sessionStorage.setItem("oidc_login_in_progress", "1");
            }
            getOidcStart()
            .then((startResponse) => {
                const targetUrl = this.resolveOidcStartTargetUrl(startResponse);
                if (!targetUrl) {
                    throw new Error("OIDC start response is missing redirect URL");
                }
                window.location.assign(targetUrl);
            })
            .catch(err => {
                this.err = err;
                this.oidcErrorMessage = String(err && err.message || "OIDC login failed");
                this.oidcStarting = false;
                this.clearOidcLoginProgressMarkers();
                localStorage.removeItem("post_login_redirect_pending");
            });
        }
    }
}
</script>

<style>
.login-page .login-method-switch .btn {
    flex: 1 1 0;
}

.login-page .ls-login-button {
    display: inline-block;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
}

.login-page .ls-login-button:disabled {
    opacity: 0.7;
    cursor: default;
}

.login-page .ls-login-button-image {
    width: 200px;
    max-width: 100%;
    height: auto;
    display: block;
}
</style>
